import React, { FC, useRef, useCallback } from 'react';

import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { isBrowser } from '@lucid/utils';
import { Player, IPlayerProps } from '@lottiefiles/react-lottie-player';
import { AnimationItem } from 'lottie-web';

import decorate from '../decorate/decorate';

export interface ScrollLottieProps extends Omit<IPlayerProps, 'src' | 'loop'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The lottie animation JSON data. */
  data?: string;
  /** The path the lottie animation JSON file. */
  path?: string;
  /** The speed at which GSAP scroll trigger should play the lottie animation. */
  scrollSpeed?: 'fast' | 'medium' | 'slow';
  /** The offset from the top where the animation will begin to play. */
  topOffset?: string;
}

const ScrollLottieComponent: FC<ScrollLottieProps> = (
  {
    autoplay = false,
    className,
    data,
    path,
    renderer = 'svg',
    scrollSpeed = 'medium',
    topOffset = 'top',
    ...rest
  }
) => {
  const lottieRef = useRef<AnimationItem>();
  const playerRef = useRef<Player>(null);
  const lottieRefs = useCallback(
    node => {
      lottieRef.current = node;
    },
    [lottieRef]
  );
  if (isBrowser()) {
    gsap.registerPlugin(ScrollTrigger);
  }
  const animationData = data
    ? JSON.parse(data)
    : path;

  const onDOMReady = () => {
    if (isBrowser()) {
      const lottieTarget = document.getElementById('lottie');
      const initialFrame = { frame: 0 };
      const initialTarget = gsap.utils.toArray(lottieTarget)[0] as ArrayLike<string | Element | null>;
      // Testing out some new speeds based on my experience playing with the scroll.
      // Once we have the real animation we'll likely need to tweak this again.
      const initialSpeed = { slow: '+=12000', medium: '+=10000', fast: '+=8000' };
      const animationScrollTrigger = {
        trigger: initialTarget,
        pin: true,
        start: `top ${topOffset}`,
        end: initialSpeed[scrollSpeed] || '+=10000',
        scrub: 1,
      };
      const totalFrames = lottieRef.current?.totalFrames || 0;

      // If the lottieTarget gets taken away by conditional rendering
      if (lottieTarget) {
        gsap.to(initialFrame, {
          frame: totalFrames - 1,
          ease: 'none',
          onUpdate: () => playerRef.current?.setSeeker(initialFrame.frame),
          scrollTrigger: animationScrollTrigger
        });
        // in case there are any other ScrollTriggers on the page and the loading of this Lottie asset caused layout changes
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
      }
    }
  };

  return (
    <Player
      {...rest}
      ref={playerRef}
      autoplay={autoplay}
      lottieRef={lottieRefs}
      onEvent={event => {
        if (event === 'load') {
          onDOMReady();
        }
      }}
      renderer={renderer}
      rendererSettings={{
        filterSize: {
          width: '200%',
          height: '200%',
          x: '-50%',
          y: '-50%',
        }
      }}
      src={animationData}
    />
  );
};

// manually adding display name or it goes missing on build
ScrollLottieComponent.displayName = 'ScrollLottie';
const ScrollLottie = decorate<ScrollLottieProps>(ScrollLottieComponent);

export { ScrollLottie };
export default ScrollLottie;
